import { Grid } from '@mui/material';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchVideoIdentificationData } from 'reducers/videoIdentificationReducer';
import { selectIsEligible } from 'selectors/partnerStatusSelector';
import { AppDispatch } from 'store';
import { Features } from 'reducers/partnerStatusReducer';
import { FeatureEligibility } from 'pco-product-service-internal-consumer';
import { cardStyle } from 'views/OrderNewProductOrService/OrderNewProductOrService';
import CardBodyComponent from '../common/card/CardBodyComponent';
import CardHeaderComponent from '../common/card/CardHeaderComponent';
import CardTooltipButton from '../common/card/CardTooltipButton';

interface VideoIdentificationProps {
  partnerId?: string;
}

const VideoIdentification = ({ partnerId }: VideoIdentificationProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const features = useSelector(selectIsEligible) as
    | (FeatureEligibility[] & { message?: string; messages?: string[] })
    | undefined;

  const videoFeature = useMemo(() => {
    return features?.find((feature) => feature.feature === Features.VIDEO_IDENTIFICATION);
  }, [features]);

  const initiateVideoIdentification = () => {
    if (partnerId) {
      dispatch(fetchVideoIdentificationData(partnerId));
    }
  };

  const title = useMemo<React.ReactNode>(() => {
    if (videoFeature?.isEligible) return '';
    const who = partnerId ? 'Partner' : 'Business relation';
    if (!videoFeature) return `${who} is not eligible for Video Identification`;
    if (videoFeature.messages?.length) {
      return (
        <>
          <div>{who} is not eligible for Video Identification:</div>
          {videoFeature.messages.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </>
      );
    }
    return `${who} is not eligible for Video Identification`;
  }, [videoFeature, partnerId]);

  return (
    <Grid item sx={cardStyle} data-testid="video-identification">
      <CardHeaderComponent title={'Video identification'} />
      <CardBodyComponent content={'Start a video identification process for your client.'} />
      <CardTooltipButton
        id={'video-verification-button'}
        data-testid={'video-verification-button'}
        buttonText={'Start video identification'}
        isEligible={videoFeature?.isEligible ?? false}
        handleClick={initiateVideoIdentification}
        tooltipTitle={title}
      />
    </Grid>
  );
};

export default VideoIdentification;
